import { supabase } from '@/config/supabase'
import {
  PROPERTY_TYPE_OPTIONS,
  CONDITION_OPTIONS,
  FLOOR_OPTIONS,
  labelOpzione,
} from '@/data/opzioni-immobile'
import type { Lead, ValuationFormData, ValuationResult } from '@/types'
// Salva lead catturato dalla chat AI
export async function saveChatLead(data: {
  name: string
  email: string
  phone: string
  zone: string
  stima: string
  sessionId: string
}): Promise<string | null> {
  if (!data.email) return null

  const nameParts = data.name.trim().split(/\s+/)
  const firstName = nameParts[0] || ''
  const lastName = nameParts.slice(1).join(' ') || ''

  const { data: result, error } = await supabase
    .from('leads_cagliari')
    .insert({
      email: data.email,
      phone: data.phone || null,
      first_name: firstName,
      last_name: lastName,
      source: 'ai_chat',
      notes: `Chat AI — Zona: ${data.zone || 'N/A'}, Stima: ${data.stima || 'N/A'}, Session: ${data.sessionId}`,
      status: 'new',
    })
    .select('id')
    .single()

  if (error) {
    console.error('Error saving chat lead:', error)
    throw error
  }

  return result.id
}

// Salva lead su Supabase
export async function saveLead(
  formData: ValuationFormData,
  valuationId?: string
): Promise<string> {
  const lead: Partial<Lead> = {
    valuation_id: valuationId,
    email: formData.email,
    phone: formData.phone,
    first_name: formData.firstName,
    last_name: formData.lastName,
    gdpr_consent: formData.gdprConsent,
    marketing_consent: formData.marketingConsent,
    status: 'new',
  }

  const { data, error } = await supabase
    .from('leads_cagliari')
    .insert(lead)
    .select('id')
    .single()

  if (error) {
    console.error('Error saving lead:', error)
    throw error
  }

  return data.id
}

// Invia notifica email per nuovo lead via Resend (serverless function)
export async function sendLeadNotification(
  formData: ValuationFormData,
  valuation: ValuationResult,
  /**
   * L'id del LEAD, non della valutazione: e' quello che finisce in
   * email_log_*.lead_id. Assente quando la persistenza e' fallita — la
   * notifica parte lo stesso, perche' in quel caso e' l'unica copia del
   * contatto.
   */
  leadId?: string | null
): Promise<void> {
  try {
    const fullName = [formData.firstName, formData.lastName].filter(Boolean).join(' ') || 'Non specificato'
    const euro = new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    })
    const formattedValue = euro.format(valuation.estimatedValue)

    const response = await fetch('/api/send-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: fullName,
        email: formData.email,
        phone: formData.phone || 'Non specificato',
        address: formData.address,
        area: formData.area,
        // Il template e' server-side e non conosce le opzioni del form: gli
        // arrivano etichette gia' risolte, non chiavi, altrimenti in mail
        // comparirebbe "da_ristrutturare".
        propertyType: labelOpzione(PROPERTY_TYPE_OPTIONS, formData.propertyType),
        condition: labelOpzione(CONDITION_OPTIONS, formData.condition),
        floor: labelOpzione(FLOOR_OPTIONS, formData.floor),
        gdprConsent: Boolean(formData.gdprConsent),
        marketingConsent: Boolean(formData.marketingConsent),
        value: formattedValue,
        minValue: euro.format(valuation.minValue),
        maxValue: euro.format(valuation.maxValue),
        pricePerSqm: euro.format(valuation.pricePerSqm),
        zone: valuation.zoneName || 'N/A',
        date: new Date().toLocaleString('it-IT', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
        leadId: leadId || null,
      }),
    })

    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      if (data.reason === 'daily_limit' || data.reason === 'monthly_limit') {
        console.warn('Email quota exceeded:', data.message)
      } else if (data.reason === 'not_configured') {
        console.warn('Notifiche email non configurate (manca RESEND_API_KEY): il lead e\' comunque salvato')
      } else if (data.reason === 'disabled') {
        console.log('Email notifications disabled server-side')
      } else {
        console.warn('Email send failed:', data)
      }
      return
    }

    console.log('Email notification sent successfully')
  } catch (error) {
    console.warn('Failed to send email notification:', error)
    // Non blocchiamo il flusso se l'email fallisce
  }
}
